<template>
  <div class="privacy-map-container">
    <!-- Placeholder State (Before Consent) -->
    <div 
      v-if="!isMapLoaded" 
      class="map-placeholder"
      :style="{ height: height, backgroundImage: placeholderImage ? `url(${placeholderImage})` : 'none' }"
      role="button"
      :aria-label="$t('loadLocation.loadMap')"
      tabindex="0"
      @click="loadMap"
      @keydown.enter="loadMap"
      @keydown.space.prevent="loadMap"
    >
      <div class="placeholder-overlay">
        <button 
          class="load-map-button"
          @click.stop="loadMap"
          :aria-label="$t('loadLocation.loadMap')"
        >
          <svg 
            width="48" 
            height="48" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            stroke-width="2"
            class="map-icon"
          >
            <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/>
            <circle cx="12" cy="10" r="3"/>
          </svg>
          <span class="button-text">{{ buttonText || $t('loadLocation.loadMap') }}</span>
        </button>
        <p class="privacy-notice">
          <svg 
            width="16" 
            height="16" 
            viewBox="0 0 24 24" 
            fill="none" 
            stroke="currentColor" 
            stroke-width="2"
            class="info-icon"
          >
            <circle cx="12" cy="12" r="10"/>
            <line x1="12" y1="16" x2="12" y2="12"/>
            <line x1="12" y1="8" x2="12.01" y2="8"/>
          </svg>
          {{ $t('loadLocation.mapPrivacyNotice') }}
        </p>
      </div>
    </div>

    <!-- Google Maps iframe (After Consent) -->
    <transition name="fade">
      <iframe
        v-if="isMapLoaded"
        :src="mapUrl"
        :height="height"
        width="100%"
        style="border:0;"
        allowfullscreen=""
        loading="lazy"
        referrerpolicy="no-referrer-when-downgrade"
        :title="$t('location.title')"
      ></iframe>
    </transition>
  </div>
</template>

<script>
export default {
  name: 'PrivacyMap',
  props: {
    mapUrl: {
      type: String,
      required: true
    },
    height: {
      type: String,
      default: '450px'
    },
    placeholderImage: {
      type: String,
      default: ''
    },
    buttonText: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      isMapLoaded: false
    }
  },
  methods: {
    loadMap() {
      this.isMapLoaded = true
      console.log('✅ User consented to load Google Maps')
    }
  }
}
</script>

<style scoped>
.privacy-map-container {
  position: relative;
  width: 100%;
  overflow: hidden;
}

.map-placeholder {
  position: relative;
  width: 100%;
  background: var(--surface-hover);
  background-size: cover;
  background-position: center;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: all 0.3s ease;
}

.map-placeholder:hover {
  background: var(--surface-muted);
}

.map-placeholder:focus {
  outline: 3px solid var(--color-accent);
  outline-offset: -3px;
}

.placeholder-overlay {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1.5rem;
  padding: 2rem;
  background: var(--surface-card);
  border-radius: 16px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.1);
  max-width: 400px;
  text-align: center;
}

.load-map-button {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.75rem;
  background: var(--cta-bg);
  color: var(--cta-fg);
  border: none;
  padding: 1.5rem 2.5rem;
  border-radius: 12px;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  box-shadow: var(--shadow-brand);
}

.load-map-button:hover {
  background: var(--color-secondary);
  transform: translateY(-2px);
  box-shadow: var(--shadow-strong);
}

.load-map-button:active {
  transform: translateY(0);
}

.load-map-button:focus {
  outline: none;
  box-shadow: var(--focus-ring);
}

.map-icon {
  width: 48px;
  height: 48px;
}

.button-text {
  font-size: 1.1rem;
}

.privacy-notice {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin: 0;
  font-size: 0.85rem;
  color: var(--text-muted);
  line-height: 1.4;
}

.info-icon {
  flex-shrink: 0;
  width: 16px;
  height: 16px;
  color: var(--color-primary);
}

/* Fade transition for iframe */
.fade-enter-active {
  animation: fadeIn 0.5s ease;
}

@keyframes fadeIn {
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
}

/* Responsive */
@media (max-width: 768px) {
  .placeholder-overlay {
    max-width: 90%;
    padding: 1.5rem;
  }

  .load-map-button {
    padding: 1.25rem 2rem;
    font-size: 1rem;
  }

  .map-icon {
    width: 40px;
    height: 40px;
  }

  .button-text {
    font-size: 1rem;
  }

  .privacy-notice {
    font-size: 0.8rem;
  }
}
</style>
